import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Grid,
  Icon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import QRCode from "react-qr-code";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppNumberPage() {
  const pageTitle = "WhatsApp Number";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [number, setNumber] = useState(null);
  const [agents, setAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");

  const [subscription, setSubscription] = useState(null);
  const [price, setPrice] = useState(0);
  const [vat, setVat] = useState(0);
  const [duration, setDuration] = useState(1);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);

  const [insights, setInsights] = useState({
    contacts: 0,
    conversations: 0,
    open_conversations: 0,
    closed_conversations: 0,
    messages: 0,
    incoming_messages: 0,
    outgoing_messages: 0,
    sent_messages: 0,
    delivered_messages: 0,
    read_messages: 0,
  });

  const [agentDialogOpen, setAgentDialogOpen] = useState(false);

  // Replace this placeholder with the public URL where the Laddar widget script is hosted.
  const WIDGET_SCRIPT_URL = "https://api.laddar.africa/v1/wacc/widget.js";

  const [entryPointDialogOpen, setEntryPointDialogOpen] = useState(false);
  const [entryPointType, setEntryPointType] = useState(null);
  const [entryPointMessage, setEntryPointMessage] = useState("");
  const [widgetLabel, setWidgetLabel] = useState("Chat with us");
  const [widgetPosition, setWidgetPosition] = useState("right");
  const [widgetColor, setWidgetColor] = useState("#25D366");

  const qrCodeRef = useRef(null);

  const whatsappPhone = String(number?.phone_number || "").replace(/\D/g, "");

  const whatsappLink = whatsappPhone
    ? `https://wa.me/${whatsappPhone}${
        entryPointMessage.trim() ? `?text=${encodeURIComponent(entryPointMessage.trim())}` : ""
      }`
    : "";

  const widgetCode = `<script
  src="${WIDGET_SCRIPT_URL}"
  data-phone="${whatsappPhone}"
  data-message="${entryPointMessage.trim().replace(/&/g, "&amp;").replace(/\"/g, "&quot;")}"
  data-label="${widgetLabel.replace(/&/g, "&amp;").replace(/\"/g, "&quot;")}"
  data-position="${widgetPosition}"
  data-color="${widgetColor}"
  defer
></script>`;

  const copyEntryPoint = async (value) => {
    try {
      await navigator.clipboard.writeText(value);

      setErrorMsg("Copied to clipboard.");
      setSeverity("success");
      setOpen(true);
    } catch (error) {
      setErrorMsg("Unable to copy. Please copy the content manually.");
      setSeverity("error");
      setOpen(true);
    }
  };

  const downloadQRCode = () => {
    const svg = qrCodeRef.current?.querySelector("svg");

    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgSource = serializer.serializeToString(svg);

    const svgBlob = new Blob([svgSource], {
      type: "image/svg+xml;charset=utf-8",
    });

    const svgUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");

      const size = 1200;
      const margin = 100;
      const qrSize = size - margin * 2;

      canvas.width = size;
      canvas.height = size;

      const context = canvas.getContext("2d");

      // White background
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, size, size);

      // Draw QR code with equal margins
      context.drawImage(image, margin, margin, qrSize, qrSize);

      canvas.toBlob((blob) => {
        if (!blob) {
          URL.revokeObjectURL(svgUrl);
          return;
        }

        const pngUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");

        anchor.href = pngUrl;
        anchor.download = `${whatsappPhone}-qr-code.png`;

        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        URL.revokeObjectURL(pngUrl);
        URL.revokeObjectURL(svgUrl);
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      setErrorMsg("Unable to generate QR code image.");
      setSeverity("error");
      setOpen(true);
    };

    image.src = svgUrl;
  };

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const formatCurrency = (amount) =>
    `₦${(Number(amount || 0) / 100).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const validDuration =
    Number.isInteger(Number(duration)) && Number(duration) >= 1 ? Number(duration) : 1;

  const subscriptionFee = Number(price || 0) * validDuration;
  const subscriptionVat = Number(vat || 0) * validDuration;
  const subscriptionTotal = subscriptionFee + subscriptionVat;

  const subscriptionIsActive =
    subscription?.expires_on && new Date(subscription.expires_on.replace(" ", "T")) > new Date();

  const fetchData = async () => {
    setShowLoading(true);

    try {
      const numberResult = await axios.get(`/portal/wacc/businesses/numbers/${ref}/fetch.json`);

      if (numberResult.data.status !== "success") {
        setShowLoading(false);

        await statusDialog.show(numberResult.data.data.message, "error", "Request Failed");

        return;
      }

      setNumber(numberResult.data.data.number);
      setAgents(numberResult.data.data.agents || []);
      setAssignedAgents(numberResult.data.data.assigned_agents || []);
      setSubscription(numberResult.data.data.subscription || null);
      setPrice(Number(numberResult.data.data.price || 0));
      setVat(Number(numberResult.data.data.vat || 0));

      const insightsResult = await axios.get(
        `/portal/wacc/businesses/numbers/${ref}/insights.json`
      );

      if (insightsResult.data.status === "success") {
        setInsights(insightsResult.data.data);
      } else {
        setErrorMsg(insightsResult.data.data.message);
        setSeverity("error");
        setOpen(true);
      }

      setPageLoaded(true);
      setShowLoading(false);
    } catch (error) {
      setShowLoading(false);

      await statusDialog.show(
        `Unable to complete request. ${
          navigator.onLine ? "" : "Make sure you have an active internet connection"
        }`,
        "error",
        "Request Failed"
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, [ref]);

  const handleSubscribe = async () => {
    const months = Number(duration);

    if (!Number.isInteger(months) || months < 1) {
      setErrorMsg("Subscription duration must be at least 1 month.");
      setSeverity("error");
      setOpen(true);
      return;
    }

    setShowLoading(true);

    try {
      const result = await axios.post("/portal/wacc/businesses/numbers/subscribe.json", {
        reference: ref,
        duration: months,
      });

      if (result.data.status === "success") {
        setErrorMsg(result.data.data.message || "Subscription successful.");
        setSeverity("success");
        setOpen(true);

        setShowSubscriptionForm(false);

        await fetchData();
      } else {
        setShowLoading(false);
        setErrorMsg(result.data.data.message);
        setSeverity("error");
        setOpen(true);
      }
    } catch (error) {
      setShowLoading(false);

      setErrorMsg(
        `Unable to complete request. ${
          navigator.onLine ? "" : "Make sure you have an active internet connection"
        }`
      );

      setSeverity("error");
      setOpen(true);
    }
  };

  const handleAddAgent = async () => {
    if (!selectedAgent) {
      return;
    }

    setAgentDialogOpen(false);
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/agents/add.json", {
        number: ref,
        agent: selectedAgent,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          const agent = result.data.data.agent;

          setAssignedAgents((current) => [...current, agent]);

          setAgents((current) => current.filter((item) => item.row !== selectedAgent));

          setSelectedAgent("");
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleRemoveAgent = async (agent) => {
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/agents/remove.json", {
        reference: agent.reference,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setAssignedAgents((current) =>
            current.filter((item) => item.reference !== agent.reference)
          );

          setAgents((current) =>
            [...current, result.data.data.agent].sort((a, b) => a.name.localeCompare(b.name))
          );
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleAgentStatus = async (agent) => {
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/agents/status.json", {
        reference: agent.reference,
        status: !agent.status,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setAssignedAgents((current) =>
            current.map((item) =>
              item.reference === agent.reference
                ? {
                    ...item,
                    status: result.data.data.agent.status,
                  }
                : item
            )
          );
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const insightCard = (title, value, icon) => (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card>
        <MDBox p={2} display="flex" alignItems="center">
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="3rem"
            height="3rem"
            borderRadius="lg"
            sx={{
              backgroundColor: "#f5f5f5",
            }}
          >
            <Icon>{icon}</Icon>
          </MDBox>

          <MDBox ml={2}>
            <MDTypography variant="text" color="text" fontSize={12}>
              {title}
            </MDTypography>

            <MDTypography variant="h6" fontWeight="bold">
              {value}
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={open} autoHideDuration={6000} onClose={() => setOpen(false)}>
        <Alert onClose={() => setOpen(false)} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* Entry Point Generator Dialog */}
      <Dialog
        open={entryPointDialogOpen}
        onClose={() => setEntryPointDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>WhatsApp Chat Entry</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDBox mb={3}>
              <MDTypography variant="body2" color="text">
                Generate ways for customers to start a WhatsApp conversation with your business.
              </MDTypography>
            </MDBox>
            {/* 

            <MDBox mt={2} mb={2}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                WhatsApp Number
              </MDTypography>

              <MDTypography variant="h6">{number?.phone_number}</MDTypography>
            </MDBox> */}

            <TextField
              fullWidth
              size="small"
              label="Pre-filled message (optional)"
              value={entryPointMessage}
              onChange={(event) => setEntryPointMessage(event.target.value)}
              multiline
              minRows={2}
              sx={{ mb: 2 }}
            />

            <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
              <MDButton
                size="small"
                variant={entryPointType === "link" ? "contained" : "outlined"}
                color="dark"
                onClick={() => setEntryPointType("link")}
                startIcon={<Icon>link</Icon>}
              >
                Chat Link
              </MDButton>

              <MDButton
                size="small"
                variant={entryPointType === "qr" ? "contained" : "outlined"}
                color="dark"
                onClick={() => setEntryPointType("qr")}
                startIcon={<Icon>qr_code</Icon>}
              >
                QR Code
              </MDButton>

              <MDButton
                size="small"
                variant={entryPointType === "widget" ? "contained" : "outlined"}
                color="dark"
                onClick={() => setEntryPointType("widget")}
                startIcon={<Icon>widgets</Icon>}
              >
                Website Widget
              </MDButton>
            </MDBox>

            <Divider sx={{ mb: 2 }} />

            {/* Chat Link */}
            {entryPointType === "link" && (
              <>
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  Generated WhatsApp Link
                </MDTypography>

                <TextField
                  fullWidth
                  size="small"
                  value={whatsappLink}
                  multiline
                  minRows={2}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 1 }}
                />

                <MDBox mt={2} display="flex" justifyContent="flex-end">
                  <MDButton
                    size="small"
                    variant="contained"
                    color="dark"
                    onClick={() => copyEntryPoint(whatsappLink)}
                    disabled={!whatsappLink}
                    startIcon={<Icon>content_copy</Icon>}
                  >
                    Copy Link
                  </MDButton>
                </MDBox>
              </>
            )}

            {/* QR Code */}
            {entryPointType === "qr" && (
              <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
                {whatsappLink ? (
                  <MDBox
                    ref={qrCodeRef}
                    p={2}
                    sx={{
                      backgroundColor: "#fff",
                      border: "1px solid #eee",
                      borderRadius: 1,
                    }}
                  >
                    <QRCode value={whatsappLink} size={220} />
                  </MDBox>
                ) : (
                  <MDTypography variant="button" color="error">
                    A valid WhatsApp number is required to generate the QR code.
                  </MDTypography>
                )}

                <MDButton
                  size="small"
                  variant="contained"
                  color="dark"
                  onClick={downloadQRCode}
                  disabled={!whatsappLink}
                  startIcon={<Icon>download</Icon>}
                >
                  Download QR Code
                </MDButton>
              </MDBox>
            )}

            {/* Website Widget */}
            {entryPointType === "widget" && (
              <>
                <MDTypography variant="caption" color="text">
                  Configure the floating website button.
                </MDTypography>

                <Grid container spacing={2} mt={0}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Button Label"
                      value={widgetLabel}
                      onChange={(event) => setWidgetLabel(event.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Position"
                      value={widgetPosition}
                      onChange={(event) => setWidgetPosition(event.target.value)}
                      SelectProps={{ native: true }}
                    >
                      <option value="right">Bottom right</option>
                      <option value="left">Bottom left</option>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="color"
                      label="Button Colour"
                      value={widgetColor}
                      onChange={(event) => setWidgetColor(event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <MDBox mt={2}>
                  <Alert severity="info">
                    <MDTypography variant="caption">
                      Copy the widget code below and paste in your website's HTML page.
                    </MDTypography>
                  </Alert>

                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={7}
                    value={widgetCode}
                    InputProps={{ readOnly: true }}
                    sx={{ mt: 2 }}
                  />
                </MDBox>

                <MDBox mt={2} display="flex" justifyContent="flex-end">
                  <MDButton
                    size="small"
                    variant="contained"
                    color="dark"
                    onClick={() => copyEntryPoint(widgetCode)}
                    startIcon={<Icon>content_copy</Icon>}
                  >
                    Copy Installation Code
                  </MDButton>
                </MDBox>
              </>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={() => setEntryPointDialogOpen(false)}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Add Agent Dialog */}
      <Dialog
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add Agent</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <Autocomplete
              fullWidth
              options={agents}
              value={agents.find((agent) => agent.row === selectedAgent) || null}
              onChange={(event, value) => {
                setSelectedAgent(value ? value.row : "");
              }}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.row === value.row}
              autoHighlight
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Agent"
                  placeholder="Search agent..."
                  size="small"
                />
              )}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={() => setAgentDialogOpen(false)}
          >
            Cancel
          </MDButton>

          <MDButton
            variant="contained"
            color="dark"
            size="small"
            disabled={!selectedAgent}
            onClick={handleAddAgent}
          >
            Add Agent
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && number && (
        <MDBox py={3}>
          <MDBox mb={2.5} display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>

            <MDBox display="flex" gap={1} flexWrap="wrap">
              {/* <MDButton
                variant="outlined"
                color="dark"
                onClick={() =>
                  document
                    .getElementById("agents")
                    ?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                }
                startIcon={<Icon>person</Icon>}
                size="small"
              >
                Agents
              </MDButton> */}

              <MDButton
                variant="contained"
                color="warning"
                onClick={() => navigate(`/wacc/businesses/number/${ref}/message-pool`)}
                startIcon={<Icon>pending</Icon>}
                size="small"
              >
                Message Queue
              </MDButton>

              <MDButton
                variant="outlined"
                color="success"
                onClick={() => navigate(`/wacc/businesses/number/${ref}/conversations`)}
                startIcon={<Icon>forum</Icon>}
                size="small"
              >
                Conversations
              </MDButton>

              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => navigate(`/wacc/businesses/number/${ref}/contacts`)}
                startIcon={<Icon>people</Icon>}
                size="small"
              >
                Contacts
              </MDButton>

              <MDButton
                variant="contained"
                color="dark"
                onClick={() => {
                  setEntryPointType("link");
                  setEntryPointDialogOpen(true);
                }}
                startIcon={<Icon>qr_code_2</Icon>}
                size="small"
              >
                Chat Entry
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mb={5}>
            <Grid container spacing={2} mb={3}>
              {insightCard("Contacts", insights.contacts, "people")}
              {insightCard("Conversations", insights.conversations, "forum")}
              {insightCard("Open Conversations", insights.open_conversations, "chat")}
              {insightCard(
                "Closed Conversations",
                insights.closed_conversations,
                "chat_bubble_outline"
              )}
            </Grid>

            <MDBox mb={2}>
              <Divider />
            </MDBox>

            <Grid container spacing={2}>
              {insightCard("Messages", insights.messages, "message")}
              {insightCard("Incoming Messages", insights.incoming_messages, "south_west")}
              {insightCard("Outgoing Messages", insights.outgoing_messages, "north_east")}
              {insightCard("Sent", insights.sent_messages, "send")}
              {insightCard("Delivered", insights.delivered_messages, "done_all")}
              {insightCard("Read", insights.read_messages, "done_all")}
            </Grid>
          </MDBox>

          <MDBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} lg={4}>
                <MDBox sx={{ height: "100%" }}>
                  <Card sx={{ height: "100%" }}>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={2}
                      >
                        <MDBox>
                          <Icon>contact_phone</Icon>

                          <MDTypography variant="h5" fontWeight="medium">
                            {number.title}
                          </MDTypography>

                          <MDTypography variant="button" color="text">
                            {number.phone_number}
                          </MDTypography>

                          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                            WhatsApp ID: {number.wa_reference}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={8} lg={8}>
                <MDBox sx={{ height: "100%" }}>
                  <Card sx={{ height: "100%" }}>
                    <MDBox p={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                        mb={2}
                      >
                        <MDTypography variant="h6">Number Subscription</MDTypography>

                        <Chip
                          size="small"
                          color={subscriptionIsActive ? "success" : "default"}
                          label={subscriptionIsActive ? "Active" : "Not Subscribed"}
                        />
                      </MDBox>

                      {subscription && (
                        <Grid container spacing={2} mb={2}>
                          <Grid item xs={12} sm={6}>
                            <MDTypography variant="caption" color="text">
                              Subscription Start
                            </MDTypography>

                            <MDTypography variant="body2" fontWeight="medium">
                              {subscription.start_time || "—"}
                            </MDTypography>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <MDTypography variant="caption" color="text">
                              Subscription Expiry
                            </MDTypography>

                            <MDTypography variant="body2" fontWeight="medium">
                              {subscription.expires_on || "—"}
                            </MDTypography>
                          </Grid>
                        </Grid>
                      )}

                      {subscriptionIsActive && !showSubscriptionForm ? (
                        <MDButton
                          variant="contained"
                          color="dark"
                          size="small"
                          startIcon={<Icon>autorenew</Icon>}
                          onClick={() => setShowSubscriptionForm(true)}
                        >
                          Renew Subscription
                        </MDButton>
                      ) : (
                        <>
                          <Divider sx={{ my: 2 }} />

                          <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={6} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Duration (Months)"
                                value={duration}
                                onChange={(event) => {
                                  const value = event.target.value;

                                  if (value === "") {
                                    setDuration("");
                                  } else {
                                    setDuration(Math.max(1, Number(value)));
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                  step: 1,
                                }}
                                helperText="Choose the number of months."
                              />
                            </Grid>

                            <Grid item xs={12} sm={6} md={8}>
                              <MDBox
                                p={2}
                                sx={{
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "8px",
                                }}
                              >
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  gap={2}
                                  mb={1}
                                >
                                  <MDTypography variant="body2" color="text">
                                    Monthly Subscription Fee
                                  </MDTypography>

                                  <MDTypography variant="body2" fontWeight="medium">
                                    {formatCurrency(price)}
                                  </MDTypography>
                                </MDBox>

                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  gap={2}
                                  mb={1}
                                >
                                  <MDTypography variant="body2" color="text">
                                    Subscription Fee ({validDuration}{" "}
                                    {validDuration === 1 ? "month" : "months"})
                                  </MDTypography>

                                  <MDTypography variant="body2" fontWeight="medium">
                                    {formatCurrency(subscriptionFee)}
                                  </MDTypography>
                                </MDBox>

                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  gap={2}
                                  mb={1}
                                >
                                  <MDTypography variant="body2" color="text">
                                    VAT
                                  </MDTypography>

                                  <MDTypography variant="body2" fontWeight="medium">
                                    {formatCurrency(subscriptionVat)}
                                  </MDTypography>
                                </MDBox>

                                <Divider sx={{ my: 1.5 }} />

                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  gap={2}
                                >
                                  <MDTypography variant="body2" fontWeight="bold">
                                    Total Amount
                                  </MDTypography>

                                  <MDTypography variant="h6" fontWeight="bold" color="dark">
                                    {formatCurrency(subscriptionTotal)}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </Grid>

                            <Grid item xs={12}>
                              <MDBox display="flex" gap={1} flexWrap="wrap">
                                <MDButton
                                  variant="contained"
                                  color="dark"
                                  size="small"
                                  startIcon={<Icon>credit_card</Icon>}
                                  onClick={handleSubscribe}
                                  disabled={
                                    showLoading ||
                                    !Number.isInteger(Number(duration)) ||
                                    Number(duration) < 1
                                  }
                                >
                                  {subscriptionIsActive ? "Confirm Renewal" : "Subscribe"}
                                </MDButton>

                                {subscriptionIsActive && (
                                  <MDButton
                                    variant="outlined"
                                    color="dark"
                                    size="small"
                                    onClick={() => setShowSubscriptionForm(false)}
                                  >
                                    Cancel
                                  </MDButton>
                                )}
                              </MDBox>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          {/*
          <MDBox mb={3}>
            <MDBox mb={2}>
              <MDTypography variant="h6">Overview</MDTypography>
            </MDBox>

            <Grid container spacing={2} mb={3}>
              {insightCard("Contacts", insights.contacts, "people")}
              {insightCard("Conversations", insights.conversations, "forum")}
              {insightCard("Open Conversations", insights.open_conversations, "chat")}
              {insightCard(
                "Closed Conversations",
                insights.closed_conversations,
                "chat_bubble_outline"
              )}
            </Grid>

            <MDBox mb={2}>
              <MDTypography variant="h6">Messages</MDTypography>
            </MDBox>

            <Grid container spacing={2}>
              {insightCard("Messages", insights.messages, "message")}
              {insightCard("Incoming Messages", insights.incoming_messages, "south_west")}
              {insightCard("Outgoing Messages", insights.outgoing_messages, "north_east")}
              {insightCard("Sent", insights.sent_messages, "send")}
              {insightCard("Delivered", insights.delivered_messages, "done_all")}
              {insightCard("Read", insights.read_messages, "done_all")}
            </Grid>
          </MDBox>
          */}

          <MDBox id="agents" mt={5} mb={3}>
            <Card>
              <MDBox p={2}>
                <MDBox mb={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDTypography variant="h6">Assigned Agents</MDTypography>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      startIcon={<Icon>person_add</Icon>}
                      onClick={() => {
                        setSelectedAgent("");
                        setAgentDialogOpen(true);
                      }}
                      disabled={agents.length === 0}
                    >
                      Add Agent
                    </MDButton>
                  </MDBox>
                </MDBox>

                <Divider sx={{ my: 3 }} />

                {assignedAgents.length === 0 ? (
                  <MDTypography variant="button" color="text">
                    No agents assigned to this number.
                  </MDTypography>
                ) : (
                  assignedAgents.map((agent) => (
                    <MDBox
                      key={agent.reference}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1.5}
                      py={1}
                      px={1}
                      sx={{
                        borderBottom: "1px solid #eee",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="button">{agent.name}</MDTypography>

                        <Chip
                          size="small"
                          label={agent.status ? "Active" : "Disabled"}
                          color={agent.status ? "success" : "default"}
                        />
                      </MDBox>

                      <MDBox display="flex" gap={1}>
                        <Tooltip title={agent.status ? "Disable Agent" : "Enable Agent"}>
                          <MDButton
                            variant="outlined"
                            color={agent.status ? "warning" : "success"}
                            size="small"
                            onClick={() => handleAgentStatus(agent)}
                            iconOnly
                          >
                            <Icon>{agent.status ? "person_off" : "person_add"}</Icon>
                          </MDButton>
                        </Tooltip>

                        <Tooltip title="Remove Agent">
                          <MDButton
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveAgent(agent)}
                            iconOnly
                          >
                            <Icon>person_remove</Icon>
                          </MDButton>
                        </Tooltip>
                      </MDBox>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppNumberPage;
